// What the agent asks the portal to do with rows the user already has: clone them, with the changes it
// proposes carried into the copies. Nothing is submitted: the clone table is the user's, and so is the Send
// for approval it ends with. Keep it import-free of React, so `node --test` can run cloneActions.test.ts
// against it directly.
//
// The proposal is CLIENT-BOUND but AGENT-SUPPLIED, so nothing in it is trusted: the rows are resolved by the
// list they belong to, every column is checked against the clone table's own editable columns, and an
// adjustment only runs when the portal itself offers that operation on that column.
//
// A refusal is the whole proposal, never the part that read cleanly. A clone opened with some of the
// asked-for changes looks like all of them, and these are rates: the user would have to already know what
// was dropped to see that it was.

import { clean, offerStore, pendingStore } from './proposalChannel.ts';

/** The key the agent's tool-call chunk carries its proposal under. */
export const OPEN_CRUD_CLONE = 'openCrudClone';

/** The action enso matches a confirmed step to. It is also the step's title, so it reads as one. */
export const OPEN_CLONE_TABLE = 'Open the clone';

/**
 * JSON Schema enso checks the call against before offering it. One argument, and a readable one: the panel
 * prints a step's arguments under its title, so anything technical in here is technical on the user's screen.
 */
export const OPEN_CLONE_PARAMETERS: Record<string, unknown> = {
  type: 'object',
  properties: { open: { type: 'string' } },
  required: ['open'],
};

/** Most rows cloned in one proposal, mirroring the ids a turn carries, and the most columns set on one. */
export const MAX_CLONE_ROWS = 25;
export const MAX_CLONE_FIELDS = 30;
export const MAX_CLONE_VALUE_LENGTH = 200;

/** How a copy relates to the row it came from: a record of its own, or one that replaces it. */
export type CloneType = 'new' | 'replacement';

/**
 * The arithmetic the portal offers on a numeric column, named as its own bulk-adjust control names it:
 * `add` adds the value, `multiply` multiplies by it, `percentage` adds that percentage of the current value.
 */
export type CloneOperation = 'add' | 'multiply' | 'percentage';

/** What a clone-table cell can be set to. Anything else is a shape no column takes. */
export type CloneValue = string | number | boolean;

/** An adjustment as it arrives on the wire: shape-checked here, allowed or refused by the grid below. */
export interface CloneAdjustment {
  column: string;
  operation: string;
  value: number;
}

/** The proposal as it arrives on the wire. Every part of it is checked before it is used. */
export interface CrudCloneInstruction {
  entity: string;
  list?: string;
  path?: string;
  /** The keys of the rows to clone. Empty means whatever the user has selected. */
  rows?: string[];
  cloneType?: CloneType;
  /** Columns set to the same value on every copy. */
  values?: Record<string, unknown>;
  /** The code each copy takes on its own, keyed by the code of the row it comes from. */
  rowCodes?: Record<string, string>;
  adjust?: CloneAdjustment;
  /** The user has agreed to be taken to this list. Without it a list that is not on screen is left alone. */
  openClone?: boolean;
}

/** One column of a clone table, as the grid on screen describes it. */
export interface CloneField {
  /** The field the row holds its value under, which is the name a proposal has to match. */
  key: string;
  /** Whether a clone lets this column be typed into at all. */
  editable: boolean;
  /** Decimals the column is kept to, so an adjusted value is the one the column would display. */
  decimalScale?: number;
  /** Operations the portal's own bulk-adjust control offers here. Absent when it offers none. */
  operations?: CloneOperation[];
  /** Bounds the portal's own control holds an adjustment to. */
  maxInput?: number;
  allowNegativeInput?: boolean;
  /** Floor on the result. When any copy would fall below it the whole batch is refused, as the control does. */
  resultMin?: number;
}

export type CloneRow = Record<string, unknown>;

/** The rows a list could resolve, and the keys it holds no row for. */
export interface ResolvedCloneRows {
  rows: CloneRow[];
  missing: string[];
}

/** What a list offers the assistant: its clone table's columns, its rows, and opening the clone. */
export interface CloneController {
  fields: () => CloneField[];
  /** The rows for these keys, or the user's current selection when none are named. */
  rows: (keys: readonly string[], signal?: AbortSignal) => Promise<ResolvedCloneRows>;
  open: (rows: CloneRow[], originals: CloneRow[], cloneType: CloneType) => void;
}

/** The refusal for a proposal whose answer the user stopped. */
export const CLONE_STOPPED = 'the answer was stopped, so nothing was cloned';

/**
 * The rows for these keys, matched without case: the ones a list already holds, and the rest through `lookUp`,
 * for a list that holds only part of its rows at a time. In the order the keys were named.
 */
export const resolveRowsByKey = async (
  keys: readonly string[],
  key: string,
  held: readonly CloneRow[],
  lookUp: (keys: string[], signal?: AbortSignal) => Promise<readonly CloneRow[]>,
  signal?: AbortSignal,
): Promise<ResolvedCloneRows> => {
  const byKey = (rows: readonly CloneRow[]) =>
    new Map(
      rows.flatMap(row => {
        const value = row[key];
        return typeof value === 'string' || typeof value === 'number' ? [[String(value).toLowerCase(), row] as const] : [];
      }),
    );
  const onHand = byKey(held);
  const unheld = keys.filter(wanted => !onHand.has(wanted.toLowerCase()));
  const lookedUp = unheld.length === 0 ? new Map<string, CloneRow>() : byKey(await lookUp(unheld, signal));
  const found = keys.map(wanted => onHand.get(wanted.toLowerCase()) ?? lookedUp.get(wanted.toLowerCase()));

  return {
    rows: found.filter((row): row is CloneRow => row !== undefined),
    missing: keys.filter((_, index) => found[index] === undefined),
  };
};

/**
 * How a mounted list is reached. A reader rather than the controller itself, for the reason the list and
 * draft registries keep one: what a page hands over is rebuilt as it renders, so an object held from mount
 * answers for the page as it was then.
 */
export type ReadCloneController = () => CloneController | null;

/** What the clone was actually opened with, once the proposal was checked against the grid. */
export interface AppliedCrudClone {
  entity: string;
  rows: number;
  cloneType: CloneType;
  /** Columns the copies open with changed, in the order they were applied. */
  changed: string[];
  /** Rows the adjustment passed over, because they hold no number in that column. */
  skipped: string[];
}

/** Either the clone was opened, or it was not and this says why, in words the user can act on. */
export type CloneOutcome = { applied: AppliedCrudClone } | { refused: string };

const OPERATIONS: readonly CloneOperation[] = ['add', 'multiply', 'percentage'];

/** The symbol the portal's own control shows for an operation, so a refusal names it the way the user sees it. */
const OPERATION_SYMBOLS: Record<CloneOperation, string> = { add: '+/-', multiply: 'x', percentage: '%' };

const readCloneType = (value: unknown): CloneType | undefined => (value === 'new' || value === 'replacement' ? value : undefined);

const readAdjustment = (value: unknown): CloneAdjustment | undefined => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return undefined;

  const { column, operation, value: amount } = value as Record<string, unknown>;
  if (typeof column !== 'string' || typeof operation !== 'string') return undefined;

  // A number is as likely to arrive quoted as bare: both mean the same to the arithmetic below.
  const parsed = typeof amount === 'number' ? amount : Number(typeof amount === 'string' ? amount.trim() : NaN);
  if (!Number.isFinite(parsed)) return undefined;

  return { column: clean(column, MAX_CLONE_VALUE_LENGTH), operation: clean(operation, MAX_CLONE_VALUE_LENGTH), value: parsed };
};

const readRowKeys = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];

  const unique = new Set<string>();
  for (const entry of value) {
    if (typeof entry !== 'string' && typeof entry !== 'number') continue;
    const key = clean(String(entry), MAX_CLONE_VALUE_LENGTH);
    if (key !== '') unique.add(key);
  }
  return [...unique].slice(0, MAX_CLONE_ROWS);
};

// Read the way the row keys are: a shape no code takes is dropped here, and the row it was meant for is
// then one the clone has no code for, which applyCrudClone refuses by name.
const readRowCodes = (value: unknown): Record<string, string> | undefined => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return undefined;

  const codes: Record<string, string> = {};
  for (const [row, code] of Object.entries(value as Record<string, unknown>)) {
    if (Object.keys(codes).length >= MAX_CLONE_ROWS) break;
    if (typeof code !== 'string' && typeof code !== 'number') continue;

    const key = clean(row, MAX_CLONE_VALUE_LENGTH);
    const wanted = clean(String(code), MAX_CLONE_VALUE_LENGTH);
    if (key === '' || wanted === '') continue;

    codes[key] = wanted;
  }

  return Object.keys(codes).length === 0 ? undefined : codes;
};

/** The proposal carried by a tool-call chunk, or null when the chunk carries none this can act on. */
export const readCrudCloneInstruction = (args: unknown): CrudCloneInstruction | null => {
  if (args === null || typeof args !== 'object') return null;

  const payload = (args as Record<string, unknown>)[OPEN_CRUD_CLONE];
  if (payload === null || typeof payload !== 'object') return null;

  const fields = payload as Record<string, unknown>;
  const { entity, list, path, rows, values, rowCodes, adjust, openClone } = fields;
  if (typeof entity !== 'string' || clean(entity, MAX_CLONE_VALUE_LENGTH) === '') return null;

  return {
    entity: clean(entity, MAX_CLONE_VALUE_LENGTH),
    list: typeof list === 'string' ? clean(list, MAX_CLONE_VALUE_LENGTH) : undefined,
    path: typeof path === 'string' ? path : undefined,
    rows: readRowKeys(rows),
    cloneType: readCloneType(fields.cloneType),
    values: values !== null && typeof values === 'object' && !Array.isArray(values) ? (values as Record<string, unknown>) : undefined,
    rowCodes: readRowCodes(rowCodes),
    adjust: readAdjustment(adjust),
    openClone: openClone === true,
  };
};

// A number reaches a numeric cell as a number: text there fails the column's own validation, and the user
// would be left correcting the assistant's typing rather than reading its proposal.
const cellValue = (value: unknown): CloneValue | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'boolean') return value;
  if (typeof value !== 'string') return null;

  const text = clean(value, MAX_CLONE_VALUE_LENGTH);
  return text === '' ? null : text;
};

// Shifting through the decimal string rather than multiplying by a power of ten, for the reason enso's own
// bulk-adjust control does it: `Math.round(1.005 * 100) / 100` is 1, because 1.005 * 100 is 100.49999999999999.
const roundToScale = (value: number, scale?: number): number => {
  if (scale === undefined) return value;
  const shifted = Number(`${value}e${scale}`);
  return Number(`${Math.round(shifted)}e-${scale}`);
};

const applyOperation = (current: number, operation: CloneOperation, input: number): number => {
  if (operation === 'add') return current + input;
  if (operation === 'multiply') return current * input;
  return current + current * (input / 100);
};

/** The field a row's code lives under, which is the one value a copy can never keep. */
const CODE_FIELD = 'code';

// How a row is named in the words the user reads, and the key an adjusted value is remembered against. A
// list keys its rows by whichever of these it carries, so the first one present is the row's name here.
const ROW_LABEL_KEYS = [CODE_FIELD, 'id', 'name'];

const rowLabel = (row: CloneRow): string => {
  for (const key of ROW_LABEL_KEYS) {
    const value = row[key];
    if (typeof value === 'string' && value.trim() !== '') return value.trim();
    if (typeof value === 'number') return String(value);
  }
  return '';
};

/** The field a list marks a row a pending change request is already changing with. */
const LOCKED_BY_KEY = 'lockedByActionId';

// A replacement gives the rows it copies an end date, so it changes them and not only the copies. A row a
// change request already holds cannot take that, and the portal refuses the submit, so the clone is refused
// here instead of opening one that cannot be sent.
const lockedRowLabels = (rows: CloneRow[]): string[] =>
  rows
    .filter(row => typeof row[LOCKED_BY_KEY] === 'string' && (row[LOCKED_BY_KEY] as string).trim() !== '')
    .map(row => rowLabel(row))
    .filter(label => label !== '');

const refuseLiterals = (
  instruction: CrudCloneInstruction,
  field: (key: string) => CloneField | undefined,
  label: string,
): { literals: Record<string, CloneValue> } | { refused: string } => {
  const literals: Record<string, CloneValue> = {};

  for (const [column, value] of Object.entries(instruction.values ?? {})) {
    if (Object.keys(literals).length >= MAX_CLONE_FIELDS)
      return { refused: `a ${label} clone was proposed with more than ${MAX_CLONE_FIELDS} columns set, so nothing was cloned` };

    const key = clean(column, MAX_CLONE_VALUE_LENGTH);
    const target = field(key);
    if (key === '' || target === undefined || !target.editable)
      return { refused: `a ${label} clone has no ${column} to set, so nothing was cloned` };

    const wanted = cellValue(value);
    if (wanted === null) return { refused: `${column} was proposed as something the clone cannot hold, so nothing was cloned` };

    literals[key] = wanted;
  }

  return { literals };
};

// The code a copy takes is never one value across the batch: every code is unique, so each copy carries its
// own, matched to the row it comes from by that row's code. Every way the codes could open a clone the
// portal will not submit is refused here rather than left to its validation: a row without a code, a code
// given twice, a copy keeping the code it came from, a copy taking another cloned row's code, and a code
// for a row that is not being cloned. The same set is refused where the proposal is built, and these are
// rates, so the browser checks them again against the rows it actually holds.
const refuseRowCodes = (
  rowCodes: Record<string, string> | undefined,
  rows: CloneRow[],
  field: (key: string) => CloneField | undefined,
  label: string,
): { codes: Map<number, string> } | { refused: string } => {
  const codes = new Map<number, string>();
  // Every copy carries a code of its own, so a proposal with none is a clone whose copies keep the codes
  // they came from. Read as nothing here whether the proposal omitted them or carried a shape no code
  // takes, and refused either way.
  if (rowCodes === undefined)
    return { refused: 'no code was proposed for the copies, and every copy needs one of its own, so nothing was cloned' };

  if (!field(CODE_FIELD)?.editable)
    return { refused: `a ${label} clone has no ${CODE_FIELD} to set, so nothing was cloned` };

  const named = new Map(Object.entries(rowCodes).map(([row, code]) => [row.toUpperCase(), code]));
  const cloned = new Set(rows.map(row => rowLabel(row).toUpperCase()));
  const taken = new Set<string>();

  for (const [index, row] of rows.entries()) {
    const name = rowLabel(row);
    if (name === '') return { refused: `a row of ${label} carries no code, so its copy cannot be given one and nothing was cloned` };

    const wanted = named.get(name.toUpperCase());
    if (wanted === undefined) return { refused: `no code was proposed for the copy of ${name}, so nothing was cloned` };

    const upper = wanted.toUpperCase();
    if (upper === name.toUpperCase())
      return { refused: `a copy cannot keep the code ${name}, since every code is unique, so nothing was cloned` };
    if (cloned.has(upper))
      return { refused: `${wanted} is already the code of a row being cloned, so nothing was cloned` };
    if (taken.has(upper)) return { refused: `${wanted} was proposed for more than one copy, so nothing was cloned` };

    taken.add(upper);
    codes.set(index, wanted);
    named.delete(name.toUpperCase());
  }

  return named.size === 0
    ? { codes }
    : { refused: `${label} has no ${[...named.keys()].join(', ')} to clone, so nothing was cloned` };
};

const refuseAdjustment = (
  adjust: CloneAdjustment,
  target: CloneField | undefined,
  label: string,
): string | null => {
  if (target === undefined || !target.editable) return `a ${label} clone has no ${adjust.column} to change, so nothing was cloned`;

  const operations = target.operations ?? [];
  if (!OPERATIONS.includes(adjust.operation as CloneOperation) || !operations.includes(adjust.operation as CloneOperation))
    return operations.length === 0
      ? `${adjust.column} cannot be adjusted here, so nothing was cloned. Set it to a value instead`
      : `${adjust.column} can only be adjusted by ${operations.map(item => OPERATION_SYMBOLS[item]).join(' or ')}, so nothing was cloned`;

  if (adjust.value < 0 && target.allowNegativeInput !== true)
    return `${adjust.column} cannot be adjusted downwards here, so nothing was cloned`;

  if (target.maxInput !== undefined && Math.abs(adjust.value) > target.maxInput)
    return `${adjust.column} cannot be adjusted by more than ${target.maxInput} at a time, so nothing was cloned`;

  return null;
};

/**
 * The clone the proposal describes, or the reason there is none. Every part is checked against the list: the
 * rows against the ones it holds, each column against the clone table's own editable columns, and an
 * adjustment against the operations and the bounds the portal offers on that column itself. A `signal` aborted
 * while the rows are being looked up cancels the lookup and opens nothing: the user stopped the answer that
 * proposed it.
 */
export const applyCrudClone = async (
  instruction: CrudCloneInstruction,
  controller: CloneController,
  signal?: AbortSignal,
): Promise<CloneOutcome> => {
  const asked = (instruction.rows ?? []).slice(0, MAX_CLONE_ROWS);
  let resolved: ResolvedCloneRows;
  try {
    resolved = await controller.rows(asked, signal);
  } catch (error) {
    if (signal?.aborted) return { refused: CLONE_STOPPED };
    throw error;
  }
  const { rows, missing } = resolved;
  const label = instruction.list ?? instruction.entity;

  if (signal?.aborted) return { refused: CLONE_STOPPED };

  if (missing.length > 0) return { refused: `${label} has no ${missing.join(', ')}, so nothing was cloned` };
  if (rows.length === 0)
    return {
      refused:
        asked.length === 0 ? `no rows are selected in ${label}, so there is nothing to clone` : `${label} has no rows to clone`,
    };

  const fields = controller.fields();
  const field = (key: string): CloneField | undefined => fields.find(item => item.key === key);

  const cloneType = instruction.cloneType ?? 'new';
  if (cloneType === 'replacement') {
    const held = lockedRowLabels(rows);
    if (held.length > 0)
      return {
        refused:
          `${held.join(', ')} ${held.length === 1 ? 'is' : 'are'} already being changed by a pending change request, so ` +
          `${held.length === 1 ? 'it' : 'they'} cannot be replaced yet. A copy that stands on its own can still be made`,
      };
  }

  const read = refuseLiterals(instruction, field, label);
  if ('refused' in read) return read;

  const { adjust } = instruction;
  // Keyed by position, not by row label: two rows can carry the same code or none at all, and a shared key
  // would write one row's adjusted value onto another's copy.
  const adjusted = new Map<number, number>();
  const skipped: string[] = [];

  if (adjust !== undefined) {
    const target = field(adjust.column);
    const refused = refuseAdjustment(adjust, target, label);
    if (refused !== null) return { refused };

    for (const [index, row] of rows.entries()) {
      const held = row[adjust.column];
      const current = Number(held);
      if (held === null || held === undefined || held === '' || Number.isNaN(current)) {
        skipped.push(rowLabel(row));
        continue;
      }

      const next = roundToScale(applyOperation(current, adjust.operation as CloneOperation, adjust.value), target?.decimalScale);
      // The floor is checked over the whole batch, as the portal's own control checks it: a value that would
      // go below it is a proposal to refuse, not a row to quietly leave out.
      if (target?.resultMin !== undefined && next < target.resultMin)
        return { refused: `that would take ${adjust.column} below ${target.resultMin}, so nothing was cloned` };

      adjusted.set(index, next);
    }

    if (adjusted.size === 0) return { refused: `none of those rows hold a ${adjust.column} to adjust, so nothing was cloned` };
  }

  if (instruction.values?.[CODE_FIELD] !== undefined)
    return { refused: `a ${CODE_FIELD} cannot be set to one value on every copy, since every code is unique, so nothing was cloned` };

  const perRow = refuseRowCodes(instruction.rowCodes, rows, field, label);
  if ('refused' in perRow) return perRow;

  const originals = rows.map(row => ({ ...row }));
  const copies = rows.map((row, index) => {
    const copy: CloneRow = { ...row, ...read.literals };
    const code = perRow.codes.get(index);
    if (code !== undefined) copy[CODE_FIELD] = code;
    const next = adjusted.get(index);
    if (adjust !== undefined && next !== undefined) copy[adjust.column] = next;
    return copy;
  });

  controller.open(copies, originals, cloneType);

  const changed = [
    ...new Set([
      ...Object.keys(read.literals),
      ...(perRow.codes.size === 0 ? [] : [CODE_FIELD]),
      ...(adjust === undefined ? [] : [adjust.column]),
    ]),
  ];
  return { applied: { entity: instruction.entity, rows: copies.length, cloneType, changed, skipped } };
};

/** What the offer would do, in the words the step shows and the user judges it by. */
export const describeCloneOffer = (instruction: CrudCloneInstruction): string => {
  const name = instruction.list ?? instruction.entity;
  const named = instruction.rows ?? [];
  const what = named.length === 0 ? `the selected rows in ${name}` : named.join(', ');

  return instruction.cloneType === 'replacement' ? `Clone ${what} as a replacement` : `Clone ${what}`;
};

/** The step's result line: what the user can see happened, including what the clone passed over. */
export const describeCloneApplied = (applied: AppliedCrudClone, listName: string | undefined): string => {
  const name = listName ?? applied.entity;
  const rows = applied.rows === 1 ? '1 row' : `${applied.rows} rows`;
  const as = applied.cloneType === 'replacement' ? ' as a replacement' : '';
  const changed = applied.changed.length === 0 ? '' : `, with ${applied.changed.join(', ')} changed`;
  const opened = `${name}: ${rows} cloned${as}${changed}`;

  return applied.skipped.length === 0 ? opened : `${opened}; ${applied.skipped.join(', ')} left unchanged`;
};

const offers = offerStore<CrudCloneInstruction>(
  instruction => describeCloneOffer(instruction),
  instruction => ({ ...instruction, openClone: true }),
);

/** The call the panel offers: one readable argument, with the proposal kept against it. */
export const offerOpenCloneCall = offers.offer;

/** The proposal behind a confirmed call, or null when it is no longer remembered. */
export const takeOpenCloneOffer = offers.take;

// A proposal for a list that is not on screen waits here until that list arrives. It survives the one
// navigation it takes to get there and nothing more: it expires, it is dropped once drained, and a newer one
// replaces it.
//
// A proposal that travels is one for rows the user has not selected, so it has to name them itself. That is
// what makes it honourable on arrival at all.

const pending = pendingStore<CrudCloneInstruction>();

/** Holds a proposal for a list that is not on screen, replacing any older one. */
export const holdCrudClone = pending.hold;

/** The proposal waiting for this list, if it is still current, with its deadline. Taking it clears it. */
export const takeCrudClone = pending.take;

export const clearPendingCrudClone = (): void => {
  pending.clear();
  offers.forget();
};

const drainOnce = async (entityType: string, read: ReadCloneController): Promise<boolean> => {
  const waiting = takeCrudClone(entityType);
  if (waiting === null) return true;

  const controller = read();
  if (controller === null) {
    holdCrudClone(waiting.instruction, waiting.expiresAt);
    return false;
  }

  try {
    await applyCrudClone(waiting.instruction, controller);
    return true;
  } catch {
    // Not held again outright: a proposal held while the rows were being looked up is the newer one.
    pending.restore(waiting.instruction, waiting.expiresAt);
    return false;
  }
};

let draining: Promise<unknown> = Promise.resolve();

/**
 * Opens the proposal waiting for this list, and says whether the caller is done. False means one is still
 * waiting, so the caller retries: until the list is mounted, or when looking its rows up failed. A proposal the
 * list refuses is settled: its rows are looked up, not waited for, so asking again gets the same answer.
 *
 * One drain runs at a time. One that starts while another is looking rows up, as a list that remounts does,
 * waits for it, and so finds the proposal the other put back.
 */
export const drainCrudClone = (entityType: string, read: ReadCloneController): Promise<boolean> => {
  const run = draining.then(() => drainOnce(entityType, read));
  draining = run;
  return run;
};
